import { useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import IconArrow from '../../components/icons/IconArrow';
import IconDelete from '../../components/icons/IconDelete';
import IconEdit from '../../components/icons/IconEdit';
import IconGrip from '../../components/icons/IconGrip';
import IconVisible from '../../components/icons/IconVisible';
import IconVisibleOff from '../../components/icons/IconVisibleOff';
import IconClock from '../../components/icons/IconClock';
import IconAccepted from '../../components/icons/IconAccepted';
import { getBlockType, BLOCK_KIND_STATIC } from '../../variables/exerciseBlockTypes';
import BLOCK_EDITOR_REGISTRY from './block-editors/registry';
import GenericBlockEditor, { renderGenericPreview } from './block-editors/GenericBlockEditor';
import { makeDndId } from '../../components/dnd/dndId';
import * as api from './api';
import { buildElementPayload, buildInteractiveElementPayload } from './exerciseTemplatePayload';

export default function BlockCard({ exerciseId, courseId, entry, canMoveUp, canMoveDown, onMove, onDeleted, onSaved }) {
   const [isEditing, setIsEditing] = useState(false);
   const { kind, block } = entry;
   const blockType = getBlockType(block.type);
   const registryEntry = BLOCK_EDITOR_REGISTRY[block.type];
   const Editor = registryEntry ? registryEntry.Editor : GenericBlockEditor;
   const renderPreview = registryEntry ? registryEntry.renderPreview : renderGenericPreview;

   // Draggable (into/out of any container) - see BlockList.jsx for the
   // DndContext this participates in, and TopicElementCard.jsx (the
   // Topic-side mirror of this exact card) for why useSortable's own
   // transform/transition are deliberately NOT applied here - only this
   // card's droppable/draggable registration is needed, reordering itself
   // is handled by the hand-rolled dragPreview overlay.
   const { attributes, listeners, setNodeRef, isDragging } = useSortable({ id: makeDndId(kind, block.id) });

   const handleDelete = () => {
      if(!window.confirm('Чи напевно хочеш видалити цей блок ?')) return;
      // Same silent-no-op-on-500 issue as TopicElementCard.jsx's own
      // handleDelete (see its comment) - this block's own child tables
      // (student_element_note, teacher_approval_request, exercise_attachment,
      // exercise_student_saved_interactive_element, student_conversation_progress)
      // RESTRICT-delete too, nothing here cascaded them.
      const del = kind === BLOCK_KIND_STATIC ? api.deleteElement(block.id) : api.deleteInteractiveElement(block.id);
      del.then(() => onDeleted()).catch(err => {
         console.error(err);
         window.alert('Не вдалося видалити блок. Можливо, він ще використовується (нотатка, збережена відповідь студента тощо).');
      });
   };

   const handleSaveChanges = (changes) => {
      const payload = kind === BLOCK_KIND_STATIC
         ? buildElementPayload(exerciseId, block, changes)
         : buildInteractiveElementPayload(exerciseId, block, changes);
      const save = kind === BLOCK_KIND_STATIC
         ? api.updateElement(block.id, payload)
         : api.updateInteractiveElement(block.id, payload);
      return save.then(() => { setIsEditing(false); onSaved(); }).catch(err => console.error(err));
   };

   // Quick toggle, independent of edit-mode - see TopicElementCard.jsx's
   // identical twin for why.
   const handleToggleBlur = (e) => {
      e.stopPropagation();
      const payload = kind === BLOCK_KIND_STATIC
         ? buildElementPayload(exerciseId, block, { blurEnabled: !block.blurEnabled })
         : buildInteractiveElementPayload(exerciseId, block, { blurEnabled: !block.blurEnabled });
      const save = kind === BLOCK_KIND_STATIC ? api.updateElement(block.id, payload) : api.updateInteractiveElement(block.id, payload);
      save.then(() => onSaved()).catch(err => console.error(err));
   };

   // See TopicElementCard.jsx's identical twin - only meaningful while
   // blurEnabled, toggles whether a student's reveal request needs a
   // teacher's Approve or resolves for them immediately.
   const handleToggleApproval = (e) => {
      e.stopPropagation();
      const nextValue = !(block.blurRequiresApproval !== false);
      const payload = kind === BLOCK_KIND_STATIC
         ? buildElementPayload(exerciseId, block, { blurRequiresApproval: nextValue })
         : buildInteractiveElementPayload(exerciseId, block, { blurRequiresApproval: nextValue });
      const save = kind === BLOCK_KIND_STATIC ? api.updateElement(block.id, payload) : api.updateInteractiveElement(block.id, payload);
      save.then(() => onSaved()).catch(err => console.error(err));
   };

   return (
      <div ref={setNodeRef} className={'exercise-block-card' + (isEditing ? ' exercise-block-card-editing' : '') + (isDragging ? ' exercise-block-card-dragging' : '')}
         style={{'--accent': blockType ? blockType.accent : '#7f7f7f'}}>
         <div className='exercise-block-card-toolbar'>
            <div className='exercise-block-drag-handle' {...attributes} {...listeners}>
               <IconGrip style={{ width: 12, height: 12 }} />
               <span className='exercise-block-card-type-label'>{blockType ? blockType.label : block.type}</span>
            </div>
            <div className='exercise-block-card-actions'>
               <div className={'exercise-block-move-btn' + (!canMoveUp ? ' disabled' : '')} onClick={() => canMoveUp && onMove(-1)} title='Вгору'>
                  <IconArrow style={{transform:'rotate(-90deg)', height:14, width:14}} />
               </div>
               <div className={'exercise-block-move-btn' + (!canMoveDown ? ' disabled' : '')} onClick={() => canMoveDown && onMove(1)} title='Вниз'>
                  <IconArrow style={{transform:'rotate(90deg)', height:14, width:14}} />
               </div>
               <div className='exercise-block-edit-btn' onClick={() => setIsEditing(prev => !prev)} title='Редагувати'>
                  <IconEdit style={{fill:'#7ec2e8', width:14, height:14, margin:0}} />
               </div>
               <div className={'exercise-block-blur-btn' + (block.blurEnabled ? ' active' : '')} onClick={handleToggleBlur}
                  title={block.blurEnabled ? 'Розмито до підтвердження - клікни щоб вимкнути' : 'Розмити до підтвердження вчителя'}>
                  {block.blurEnabled ? <IconVisibleOff style={{ width: 14, height: 14, margin: 0 }} /> : <IconVisible style={{ width: 14, height: 14, margin: 0 }} />}
               </div>
               {block.blurEnabled ? (
                  <div className={'exercise-block-approval-btn' + (block.blurRequiresApproval === false ? ' auto' : '')} onClick={handleToggleApproval}
                     title={block.blurRequiresApproval === false
                        ? 'Одразу без підтвердження - клікни щоб вимагати підтвердження вчителя'
                        : 'Вимагає підтвердження вчителя - клікни щоб відкривати одразу без підтвердження'}>
                     {block.blurRequiresApproval === false ? <IconAccepted style={{ width: 14, height: 14, margin: 0 }} /> : <IconClock style={{ width: 14, height: 14, margin: 0 }} />}
                  </div>
               ) : ''}
               <div className='exercise-block-delete-btn' onClick={handleDelete} title='Видалити'>
                  <IconDelete style={{fill:'#e0876c', width:14, height:14, margin:0}} />
               </div>
            </div>
         </div>
         <div className='exercise-block-card-body'>
            {isEditing ? (
               <Editor block={block} courseId={courseId} onSave={handleSaveChanges} onCancel={() => setIsEditing(false)} />
            ) : renderPreview(block)}
         </div>
      </div>
   );
}
